import { calculateSchedule } from './trainingReportForm';
import { getAttendanceDates } from './trainingReportAttendance';
import { MAX_TRAINING_SESSIONS } from './trainingReportPolicy';

const REQUIRED_GENERAL_FIELDS = [
  'nombre_accion',
  'programa',
  'modalidad',
  'nombre_instructor',
  'fecha_inicio',
  'fecha_fin',
  'dias',
  'hora_inicio',
  'hora_fin',
  'duracion_programada_minutos',
  'coordinador_nombre',
  'coordinador_cargo',
];

const isMissing = (value) =>
  value === null ||
  value === undefined ||
  (typeof value === 'string' && !value.trim());

const attendanceKey = (participantId, date) =>
  `${Number(participantId)}|${String(date || '').slice(0, 10)}`;

export const buildTrainingReportReview = (
  report = {},
  summary = {},
  unavailableEvidenceIds = [],
) => {
  const blockers = [];
  const warnings = [];
  const participants = report.participantes || [];
  const attendances = report.asistencias || [];
  const evidences = report.evidencias || [];
  const dates = getAttendanceDates(report);

  const missingGeneral = REQUIRED_GENERAL_FIELDS.filter((field) =>
    isMissing(report[field]),
  );
  if (missingGeneral.length > 0) {
    blockers.push({
      code: 'GENERAL_DATA_INCOMPLETE',
      section: 'Datos generales',
      message: 'Faltan datos generales obligatorios.',
      detail: `${missingGeneral.length} campo(s) pendiente(s).`,
    });
  }

  if (participants.length === 0) {
    blockers.push({
      code: 'NO_PARTICIPANTS',
      section: 'Participantes',
      message: 'El reporte no tiene participantes.',
    });
  } else {
    const genderPending = participants.filter(
      (participant) => !['H', 'M'].includes(participant.genero),
    ).length;
    if (genderPending > 0) {
      blockers.push({
        code: 'GENDER_PENDING',
        section: 'Participantes',
        message: 'Faltan géneros por capturar.',
        detail: `${genderPending} participante(s) pendiente(s).`,
      });
    }

    const resultPending = participants.filter(
      (participant) =>
        isMissing(participant.calificacion_diagnostica) ||
        isMissing(participant.calificacion_final),
    ).length;
    if (resultPending > 0) {
      blockers.push({
        code: 'RESULTS_PENDING',
        section: 'Resultados',
        message: 'Faltan resultados diagnósticos o finales.',
        detail: `${resultPending} participante(s) pendiente(s).`,
      });
    }
  }

  if (dates.length > MAX_TRAINING_SESSIONS) {
    blockers.push({
      code: 'SESSION_LIMIT_EXCEEDED',
      section: 'Asistencia',
      message: `El reporte supera el máximo de ${MAX_TRAINING_SESSIONS} sesiones.`,
      detail: `${dates.length} sesiones registradas.`,
    });
  }

  if (attendances.length === 0) {
    blockers.push({
      code: 'ATTENDANCE_NOT_GENERATED',
      section: 'Asistencia',
      message: 'No se han generado asistencias.',
    });
  } else {
    const pendingAttendance = attendances.filter(
      (attendance) => attendance.asistio === null || attendance.asistio === undefined,
    ).length;
    if (pendingAttendance > 0) {
      blockers.push({
        code: 'ATTENDANCE_PENDING',
        section: 'Asistencia',
        message: 'Faltan asistencias SI/NO por capturar.',
        detail: `${pendingAttendance} celda(s) pendiente(s).`,
      });
    }

    const participantIds = participants
      .map((participant) => Number(participant.id_reporte_participante))
      .filter((id) => Number.isInteger(id) && id > 0);
    const keys = new Set();
    let duplicate = false;
    attendances.forEach((attendance) => {
      const key = attendanceKey(
        attendance.id_reporte_participante,
        attendance.fecha,
      );
      if (keys.has(key)) duplicate = true;
      keys.add(key);
    });
    const expected = participantIds.length * dates.length;
    const incompleteGrid =
      duplicate ||
      dates.length === 0 ||
      keys.size !== expected ||
      participantIds.some((participantId) =>
        dates.some((date) => !keys.has(attendanceKey(participantId, date))),
      );
    if (incompleteGrid) {
      blockers.push({
        code: 'ATTENDANCE_GRID_INCOMPLETE',
        section: 'Asistencia',
        message: 'La cuadrícula de asistencias está incompleta.',
      });
    }
  }

  if (evidences.length === 0) {
    blockers.push({
      code: 'EVIDENCE_REQUIRED',
      section: 'Evidencias',
      message: 'Falta al menos una evidencia fotográfica.',
    });
  }

  if (unavailableEvidenceIds.length > 0) {
    blockers.push({
      code: 'EVIDENCE_FILE_UNAVAILABLE',
      section: 'Evidencias',
      message: 'Hay archivos de evidencia no disponibles.',
      detail: `${unavailableEvidenceIds.length} archivo(s) no pudieron verificarse.`,
    });
  }

  const reconciledCount = participants.filter(
    (participant) =>
      participant.horas_conciliadas_minutos !== null &&
      participant.horas_conciliadas_minutos !== undefined,
  ).length;
  if (reconciledCount > 0) {
    warnings.push({
      code: 'RECONCILED_HOURS_USED',
      section: 'Asistencia',
      message: 'Se utilizaron horas conciliadas manualmente.',
      detail: `${reconciledCount} participante(s) con ajuste manual.`,
    });
  }

  const schedule = calculateSchedule({
    fecha_inicio: String(report.fecha_inicio || '').slice(0, 10),
    fecha_fin: String(report.fecha_fin || '').slice(0, 10),
    dias: String(report.dias || '')
      .split(',')
      .map((day) => day.trim().toLowerCase())
      .filter(Boolean),
    hora_inicio: String(report.hora_inicio || '').slice(0, 5),
    hora_fin: String(report.hora_fin || '').slice(0, 5),
  });
  const storedDuration = Number(report.duracion_programada_minutos);
  if (
    schedule.totalMinutes > 0 &&
    Number.isFinite(storedDuration) &&
    storedDuration !== schedule.totalMinutes
  ) {
    warnings.push({
      code: 'MANUAL_DURATION',
      section: 'Datos generales',
      message: 'La duración reportada fue ajustada manualmente.',
      detail: `Calculada: ${(schedule.totalMinutes / 60).toFixed(2).replace(/\.00$/, '')} h · Reportada: ${(storedDuration / 60).toFixed(2).replace(/\.00$/, '')} h.`,
    });
  }

  const npCount = Number(summary.resultados_finales_np || 0);
  const bCount = Number(summary.resultados_finales_b || 0);
  if (npCount > 0 || bCount > 0) {
    warnings.push({
      code: 'SPECIAL_RESULTS',
      section: 'Resultados',
      message: 'El reporte contiene resultados especiales.',
      detail: `NP: ${npCount} · B: ${bCount}.`,
    });
  }

  warnings.push({
    code: 'EXP_LABOR_PROVISIONAL',
    section: 'Participantes',
    message: '“Exp. labor.” usa provisionalmente la matrícula del participante.',
    detail: 'Esta equivalencia debe validarse antes de cerrar el formato definitivo con el cliente.',
  });

  return {
    blockers,
    warnings,
    ready: blockers.length === 0,
    metrics: {
      participants: participants.length,
      sessions: dates.length,
      attendanceCells: attendances.length,
      evidences: evidences.length,
      average:
        summary.promedio_grupal === null || summary.promedio_grupal === undefined
          ? null
          : Number(summary.promedio_grupal),
      men: Number(summary.total_hombres || 0),
      women: Number(summary.total_mujeres || 0),
    },
  };
};
